/**
 * Soil Temperature Transform Utilities
 * Handles multi-year averaging and day-of-year calculations for historical comparisons
 */

export type WeatherSeries = {
  time: string[]
  t0: number[]
  t6: number[]
}

export interface DailyAverage {
  dayOfYear: number
  date: string // MM-DD format for display
  avgTemp: number
  minTemp: number
  maxTemp: number
  sampleCount: number
}

export interface ChartDataPoint {
  date: string // ISO date string
  dayOfYear: number
  current?: number | null
  fiveYearAvg?: number | null
  tenYearAvg?: number | null
}

/**
 * Get the day of year (1-366) for a given date
 * Uses UTC to avoid timezone issues when parsing date strings
 */
export function getDayOfYear(date: Date): number {
  // Use UTC methods to avoid timezone issues
  const year = date.getUTCFullYear()
  const month = date.getUTCMonth()
  const day = date.getUTCDate()

  // Calculate day of year using UTC
  const startOfYear = Date.UTC(year, 0, 1)
  const currentDate = Date.UTC(year, month, day)
  const diff = currentDate - startOfYear
  const oneDay = 1000 * 60 * 60 * 24

  return Math.floor(diff / oneDay) + 1 // +1 because Jan 1 = day 1, not day 0
}

/**
 * Get day of year from a date string like "2024-12-22" or "2024-12-22T12:00"
 * This is more reliable than parsing to Date and calling getDayOfYear
 */
export function getDayOfYearFromString(dateStr: string): number {
  // Extract just the date part (YYYY-MM-DD)
  const datePart = dateStr.split("T")[0]
  const [year, month, day] = datePart.split("-").map(Number)

  // Calculate day of year
  const startOfYear = Date.UTC(year, 0, 1)
  const currentDate = Date.UTC(year, month - 1, day) // month is 0-indexed
  const diff = currentDate - startOfYear
  const oneDay = 1000 * 60 * 60 * 24

  return Math.floor(diff / oneDay) + 1
}

/**
 * Extract MM-DD from a date string like "2024-12-22" or "2024-12-22T12:00"
 * This is used to group data across years regardless of leap year differences
 */
export function getMMDDFromString(dateStr: string): string {
  const datePart = dateStr.split("T")[0]
  const [, month, day] = datePart.split("-")
  return `${month}-${day}`
}

/**
 * Convert day of year back to MM-DD format
 */
export function dayOfYearToMMDD(dayOfYear: number, isLeapYear: boolean = false): string {
  // Use a reference year (2024 is a leap year, 2023 is not)
  const year = isLeapYear ? 2024 : 2023
  const date = new Date(year, 0, dayOfYear)
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${month}-${day}`
}

/**
 * Aggregate hourly data to daily averages
 * Takes hourly time series and returns daily values
 */
export function aggregateToDaily(
  time: string[],
  values: number[]
): { dates: string[]; avgTemps: number[] } {
  const dailyMap = new Map<string, number[]>()

  time.forEach((timestamp, i) => {
    const temp = values[i]
    if (temp === null || temp === undefined || isNaN(temp)) return

    const dateKey = timestamp.split("T")[0] // YYYY-MM-DD
    if (!dailyMap.has(dateKey)) {
      dailyMap.set(dateKey, [])
    }
    dailyMap.get(dateKey)!.push(temp)
  })

  const dates: string[] = []
  const avgTemps: number[] = []

  // Sort by date
  const sortedEntries = Array.from(dailyMap.entries()).sort((a, b) =>
    a[0].localeCompare(b[0])
  )

  for (const [date, temps] of sortedEntries) {
    if (temps.length > 0) {
      dates.push(date)
      avgTemps.push(
        Number((temps.reduce((a, b) => a + b, 0) / temps.length).toFixed(1))
      )
    }
  }

  return { dates, avgTemps }
}

/**
 * Calculate day-of-year averages from multiple years of data
 * This is the core function for computing 5-year and 10-year historical averages
 *
 * IMPORTANT: We group by MM-DD (not dayOfYear) to handle leap year differences.
 * March 1 in a leap year is day 61, but in a non-leap year it's day 60.
 * By grouping by MM-DD, we correctly align dates across years.
 */
export function calculateMultiYearAverage(
  yearlyData: WeatherSeries[],
  depth: "t0" | "t6" = "t6"
): DailyAverage[] {
  // Group temperatures by MM-DD to handle leap year differences correctly
  const dayGroups = new Map<string, number[]>()

  for (const yearData of yearlyData) {
    if (!yearData.time || !yearData[depth]) continue

    yearData.time.forEach((timestamp, i) => {
      const temp = yearData[depth][i]
      if (temp === null || temp === undefined || isNaN(temp)) return

      // Use MM-DD as the key to align dates across leap/non-leap years
      const mmdd = getMMDDFromString(timestamp)

      if (!dayGroups.has(mmdd)) {
        dayGroups.set(mmdd, [])
      }
      dayGroups.get(mmdd)!.push(temp)
    })
  }

  // Calculate averages for each day
  const result: DailyAverage[] = []

  for (const [mmdd, temps] of dayGroups) {
    if (temps.length === 0) continue

    const avgTemp = temps.reduce((a, b) => a + b, 0) / temps.length
    const minTemp = Math.min(...temps)
    const maxTemp = Math.max(...temps)

    // Calculate dayOfYear using a reference non-leap year (2023) for consistent sorting
    const refDate = `2023-${mmdd}`
    const dayOfYear = getDayOfYearFromString(refDate)

    result.push({
      dayOfYear,
      date: mmdd,
      avgTemp: Number(avgTemp.toFixed(1)),
      minTemp: Number(minTemp.toFixed(1)),
      maxTemp: Number(maxTemp.toFixed(1)),
      sampleCount: temps.length,
    })
  }

  // Sort by day of year
  return result.sort((a, b) => a.dayOfYear - b.dayOfYear)
}

/**
 * Merge current year data with historical averages for chart display
 * Aligns data by MM-DD so lines can be compared across leap/non-leap years
 */
export function mergeForChart(
  currentData: { dates: string[]; temps: number[] },
  fiveYearAvg?: DailyAverage[],
  tenYearAvg?: DailyAverage[]
): ChartDataPoint[] {
  // Create lookup maps for historical averages using MM-DD (date field)
  const fiveYearMap = new Map<string, number>()
  const tenYearMap = new Map<string, number>()

  fiveYearAvg?.forEach((d) => fiveYearMap.set(d.date, d.avgTemp))
  tenYearAvg?.forEach((d) => tenYearMap.set(d.date, d.avgTemp))

  // Build chart data from current year dates
  const chartData: ChartDataPoint[] = []

  currentData.dates.forEach((date, i) => {
    // Extract MM-DD to look up historical averages
    const mmdd = getMMDDFromString(date)
    const dayOfYear = getDayOfYearFromString(date)

    chartData.push({
      date,
      dayOfYear,
      current: currentData.temps[i] ?? null,
      fiveYearAvg: fiveYearMap.get(mmdd) ?? null,
      tenYearAvg: tenYearMap.get(mmdd) ?? null,
    })
  })

  return chartData
}

/**
 * Get lawn care recommendation based on soil temperature and grass type
 */
export type GrassCategory = "cool" | "warm"

export interface SoilTempRecommendation {
  tempRange: string
  status: "dormant" | "caution" | "optimal" | "warning"
  title: string
  description: string
}

export function getSoilTempRecommendation(
  temp: number,
  grassCategory: GrassCategory = "cool"
): SoilTempRecommendation {
  if (grassCategory === "cool") {
    // Cool-season grass recommendations
    if (temp < 40) {
      return {
        tempRange: "< 40°F",
        status: "dormant",
        title: "Dormant Period",
        description:
          "Grass is dormant. Avoid foot traffic on frozen turf. No fertilization or seeding.",
      }
    } else if (temp < 50) {
      return {
        tempRange: "40-50°F",
        status: "caution",
        title: "Pre-Emergent Window",
        description:
          "Apply pre-emergent herbicide before soil reaches 55°F. Crabgrass begins germinating soon.",
      }
    } else if (temp < 55) {
      return {
        tempRange: "50-55°F",
        status: "optimal",
        title: "Optimal for Overseeding",
        description:
          "Ideal soil temperature for cool-season grass seed germination. Begin spring overseeding.",
      }
    } else if (temp < 65) {
      return {
        tempRange: "55-65°F",
        status: "optimal",
        title: "Peak Root Growth",
        description:
          "Excellent conditions for root development. Apply slow-release fertilizer for best results.",
      }
    } else if (temp < 70) {
      return {
        tempRange: "65-70°F",
        status: "caution",
        title: "Transition Period",
        description:
          "Reduce fertilization frequency. Grass growth slowing as temperatures rise.",
      }
    } else if (temp < 80) {
      return {
        tempRange: "70-80°F",
        status: "warning",
        title: "Heat Stress Risk",
        description:
          "Cool-season grasses stressed. Water deeply and infrequently. Raise mowing height.",
      }
    } else {
      return {
        tempRange: "> 80°F",
        status: "warning",
        title: "Summer Dormancy",
        description:
          "Avoid seeding and heavy fertilization. Focus on maintaining adequate moisture.",
      }
    }
  } else {
    // Warm-season grass recommendations
    if (temp < 50) {
      return {
        tempRange: "< 50°F",
        status: "dormant",
        title: "Dormant Period",
        description:
          "Warm-season grass is dormant. Avoid traffic on turf. No fertilization needed.",
      }
    } else if (temp < 60) {
      return {
        tempRange: "50-60°F",
        status: "caution",
        title: "Pre-Emergent Window",
        description:
          "Apply pre-emergent herbicide before active growth begins. Watch for green-up signs.",
      }
    } else if (temp < 70) {
      return {
        tempRange: "60-70°F",
        status: "optimal",
        title: "Green-Up Period",
        description:
          "Grass emerging from dormancy. Begin light fertilization as green-up progresses.",
      }
    } else if (temp < 80) {
      return {
        tempRange: "70-80°F",
        status: "optimal",
        title: "Optimal Growth",
        description:
          "Peak growth conditions. Ideal for fertilization, overseeding, and lawn renovation.",
      }
    } else if (temp < 90) {
      return {
        tempRange: "80-90°F",
        status: "optimal",
        title: "Active Growth",
        description:
          "Excellent growing conditions. Maintain regular mowing and irrigation schedule.",
      }
    } else {
      return {
        tempRange: "> 90°F",
        status: "caution",
        title: "Heat Management",
        description:
          "Reduce mowing frequency. Water early morning. Some stress normal at peak summer.",
      }
    }
  }
}

/**
 * Aggregate chart data to monthly averages
 * Used for displaying cleaner charts over long date ranges (> 90 days)
 */
export function aggregateChartToMonthly(
  data: ChartDataPoint[]
): ChartDataPoint[] {
  if (data.length === 0) return data

  // Group by year-month
  const monthlyGroups = new Map<
    string,
    {
      current: number[]
      fiveYearAvg: number[]
      tenYearAvg: number[]
      dates: string[]
    }
  >()

  for (const point of data) {
    // Extract year-month (YYYY-MM)
    const yearMonth = point.date.slice(0, 7)

    if (!monthlyGroups.has(yearMonth)) {
      monthlyGroups.set(yearMonth, {
        current: [],
        fiveYearAvg: [],
        tenYearAvg: [],
        dates: [],
      })
    }

    const group = monthlyGroups.get(yearMonth)!
    group.dates.push(point.date)

    if (point.current !== null && point.current !== undefined) {
      group.current.push(point.current)
    }
    if (point.fiveYearAvg !== null && point.fiveYearAvg !== undefined) {
      group.fiveYearAvg.push(point.fiveYearAvg)
    }
    if (point.tenYearAvg !== null && point.tenYearAvg !== undefined) {
      group.tenYearAvg.push(point.tenYearAvg)
    }
  }

  // Calculate monthly averages
  const result: ChartDataPoint[] = []

  // Sort by year-month
  const sortedMonths = Array.from(monthlyGroups.keys()).sort()

  for (const yearMonth of sortedMonths) {
    const group = monthlyGroups.get(yearMonth)!

    // Use the middle date of the month for display
    const middleIndex = Math.floor(group.dates.length / 2)
    const representativeDate = group.dates[middleIndex] || group.dates[0]

    const avgCurrent =
      group.current.length > 0
        ? Number(
            (group.current.reduce((a, b) => a + b, 0) / group.current.length).toFixed(1)
          )
        : null

    const avgFiveYear =
      group.fiveYearAvg.length > 0
        ? Number(
            (group.fiveYearAvg.reduce((a, b) => a + b, 0) / group.fiveYearAvg.length).toFixed(1)
          )
        : null

    const avgTenYear =
      group.tenYearAvg.length > 0
        ? Number(
            (group.tenYearAvg.reduce((a, b) => a + b, 0) / group.tenYearAvg.length).toFixed(1)
          )
        : null

    result.push({
      date: representativeDate,
      dayOfYear: getDayOfYearFromString(representativeDate),
      current: avgCurrent,
      fiveYearAvg: avgFiveYear,
      tenYearAvg: avgTenYear,
    })
  }

  return result
}

/**
 * Calculate temperature difference from historical average
 */
export function getTempDifference(
  current: number,
  historical: number
): { diff: number; label: string; trend: "warmer" | "cooler" | "normal" } {
  const diff = Number((current - historical).toFixed(1))
  const absDiff = Math.abs(diff)

  let trend: "warmer" | "cooler" | "normal"
  let label: string

  if (absDiff < 2) {
    trend = "normal"
    label = "Near average"
  } else if (diff > 0) {
    trend = "warmer"
    label = `${absDiff}°F warmer than average`
  } else {
    trend = "cooler"
    label = `${absDiff}°F cooler than average`
  }

  return { diff, label, trend }
}
